/**
 * Staff awards panel (docs/Decisions.md D42): find a member, then award one
 * of the organization's designs at a level, change the level, or revoke it,
 * and record an investment milestone (which earns the First Investment
 * ribbon). Every action is audit-logged server side.
 */
import { useEffect, useState } from 'react';
import { ui, cn } from '../../lib/ui';
import * as ribbonsApi from '../../api/ribbons';
import { ErrorAlert, UserSearchPicker } from '../ui';
import { useToast } from '../toast';
import RibbonRack from './RibbonRack';
import { TextInput, SelectInput, PremiumNotice } from './parts';

export default function AwardsPanel({ isOrgAdmin }) {
  const [data, setData] = useState(null);
  const [member, setMember] = useState(null);
  const [ribbonId, setRibbonId] = useState('');
  const [levelIndex, setLevelIndex] = useState(0);
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [rackKey, setRackKey] = useState(0);
  const toast = useToast();

  useEffect(() => {
    ribbonsApi.listDefinitions().then(setData).catch(setError);
  }, []);

  const active = data ? data.definitions.filter(d => d.status === 'active') : [];
  const chosen = active.find(d => d.ribbonId === ribbonId) || null;

  async function run(action, message) {
    setBusy(true);
    setError(null);
    try {
      await action();
      toast.success(message);
      setRackKey(k => k + 1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const payload = () => ({
    userId: member.userId,
    ribbonId,
    levelIndex: chosen && chosen.levels.length ? levelIndex : undefined,
    reason: reason.trim() || undefined,
  });

  if (error && !data) return <ErrorAlert error={error} />;
  if (!data) {
    return (
      <p className={ui.muted} role="status">
        Loading
      </p>
    );
  }
  if (!data.canAward) return <p className={ui.muted}>Only organization staff can award ribbons.</p>;

  return (
    <div className="flex flex-col gap-6">
      <section className={ui.card} aria-labelledby="awards-member-heading">
        <h3 id="awards-member-heading" className={ui.h3}>
          Member
        </h3>
        {member ? (
          <div className={cn(ui.row, ui.spread)}>
            <span className="font-semibold text-dcon-ink">
              {member.displayName} <span className={ui.muted}>{member.email}</span>
            </span>
            <button
              type="button"
              className={cn(ui.btnSecondary, ui.btnSm)}
              onClick={() => setMember(null)}
            >
              Choose another member
            </button>
          </div>
        ) : (
          <UserSearchPicker onPick={setMember} />
        )}
      </section>

      {member && (
        <>
          <ErrorAlert error={error} />
          {data.features.ribbonDesigner ? (
            <section className={ui.card} aria-labelledby="awards-ribbon-heading">
              <h3 id="awards-ribbon-heading" className={ui.h3}>
                Organization ribbons
              </h3>
              {active.length === 0 ? (
                <p className={ui.muted}>Design a ribbon first, on the Designer tab.</p>
              ) : (
                <>
                  <div className={cn(ui.row, 'items-end')}>
                    <SelectInput
                      label="Ribbon"
                      value={ribbonId}
                      onChange={e => {
                        setRibbonId(e.target.value);
                        setLevelIndex(0);
                      }}
                    >
                      <option value="">Choose a ribbon</option>
                      {active.map(d => (
                        <option key={d.ribbonId} value={d.ribbonId}>
                          {d.name}
                        </option>
                      ))}
                    </SelectInput>
                    {chosen && chosen.levels.length > 0 && (
                      <SelectInput
                        label="Level"
                        value={levelIndex}
                        onChange={e => setLevelIndex(parseInt(e.target.value, 10))}
                      >
                        {chosen.levels.map((l, i) => (
                          <option key={i} value={i}>
                            {l.name}
                          </option>
                        ))}
                      </SelectInput>
                    )}
                    <TextInput
                      label="Reason (optional)"
                      value={reason}
                      maxLength={200}
                      onChange={e => setReason(e.target.value)}
                    />
                  </div>
                  <div className={ui.row}>
                    <button
                      type="button"
                      className={ui.btnPrimary}
                      disabled={busy || !chosen}
                      onClick={() => run(() => ribbonsApi.awardRibbon(payload()), 'Ribbon awarded')}
                    >
                      Award
                    </button>
                    {chosen && chosen.levels.length > 0 && (
                      <button
                        type="button"
                        className={ui.btnSecondary}
                        disabled={busy}
                        onClick={() =>
                          run(
                            () => ribbonsApi.setRibbonLevel({ ...payload(), levelIndex }),
                            'Level updated'
                          )
                        }
                      >
                        Set level
                      </button>
                    )}
                    <button
                      type="button"
                      className={ui.btnDanger}
                      disabled={busy || !chosen}
                      onClick={() =>
                        run(
                          () =>
                            ribbonsApi.revokeRibbon({
                              userId: member.userId,
                              ribbonId,
                              reason: reason.trim() || undefined,
                            }),
                          'Ribbon revoked'
                        )
                      }
                    >
                      Revoke
                    </button>
                  </div>
                </>
              )}
            </section>
          ) : (
            <PremiumNotice isOrgAdmin={isOrgAdmin} />
          )}

          <section className={ui.card} aria-labelledby="awards-milestone-heading">
            <h3 id="awards-milestone-heading" className={ui.h3}>
              Record an investment
            </h3>
            <p className={cn(ui.muted, 'mb-4 text-body-s')}>
              Counts toward the First Investment ribbon and any investment ribbon your organization
              designed. A reference such as a deal id stops the same investment counting twice.
            </p>
            <div className={cn(ui.row, 'items-end')}>
              <TextInput
                label="Reference"
                value={reference}
                maxLength={200}
                onChange={e => setReference(e.target.value)}
              />
              <TextInput
                label="Note (optional)"
                value={note}
                maxLength={500}
                onChange={e => setNote(e.target.value)}
              />
            </div>
            <button
              type="button"
              className={ui.btnSecondary}
              disabled={busy}
              onClick={() =>
                run(
                  () =>
                    ribbonsApi.recordMilestone({
                      userId: member.userId,
                      metric: 'investments_made',
                      note: note.trim() || undefined,
                      reference: reference.trim() || undefined,
                    }),
                  'Investment recorded'
                )
              }
            >
              Record investment
            </button>
          </section>

          <RibbonRack
            key={`${member.userId}-${rackKey}`}
            userId={member.userId}
            title={`${member.displayName}'s rack`}
          />
        </>
      )}
    </div>
  );
}
